import { create } from 'zustand';
import { api, setSessionEndedHandler } from '@/lib/api/core';
import { analytics, EVENTS } from '@/lib/analytics';
import type { User } from '@/types/core';
import { logError } from '@/lib/logger';
import { useOrgStore } from '@/store/orgStore';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingIn: boolean;
  login: (code: string, state?: string, attribution?: Record<string, string>) => Promise<void>;
  loginAdo: (code: string, state?: string, attribution?: Record<string, string>) => Promise<void>;
  logout: () => void;
  switchAccount: () => Promise<void>;
  loadUser: () => Promise<void>;
  clearSession: () => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  clearLoggingIn: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: localStorage.getItem('access_token'),
  isAuthenticated: !!localStorage.getItem('access_token'),
  isLoading: false,
  isLoggingIn: false,

  login: async (code: string, state?: string, attribution?: Record<string, string>) => {
    set({ isLoading: true, isLoggingIn: true });
    try {
      const response = await api.githubCallback(code, state, attribution);
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      set({
        accessToken: response.access_token,
        isAuthenticated: true,
        user: response.user,
        isLoading: false,
        // Note: isLoggingIn stays true until navigation completes
      });
    } catch (error) {
      logError('Login failed:', error);
      set({ isLoading: false, isLoggingIn: false });
      throw error;
    }
  },

  loginAdo: async (code: string, state?: string, attribution?: Record<string, string>) => {
    set({ isLoading: true, isLoggingIn: true });
    try {
      const response = await api.adoCallback(code, state, attribution);
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      set({
        accessToken: response.access_token,
        isAuthenticated: true,
        user: response.user,
        isLoading: false,
        // Note: isLoggingIn stays true until navigation completes
      });

      // ADO callback returns minimal org objects (id, name, slug, provider_type).
      // Full org data is fetched by OnboardingRouter via api.getOrganizations().
      if (response.organizations && response.organizations.length > 0) {
        const orgStore = useOrgStore.getState();
        orgStore.setOrganizations(response.organizations);
        orgStore.setCurrentOrg(response.organizations[0]);
      }
    } catch (error) {
      logError('ADO login failed:', error);
      set({ isLoading: false, isLoggingIn: false });
      throw error;
    }
  },

  logout: () => {
    // Track logout event before clearing data
    analytics.track({ name: EVENTS.LOGOUT });
    analytics.reset();

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    // The org goes with the session. Left behind, it made /pricing and /demo
    // fire authenticated requests that bounced the logged-out visitor to /login.
    useOrgStore.getState().reset();
    set({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoggingIn: false,
    });
  },

  switchAccount: async () => {
    // Track account switch event before clearing data
    analytics.track({ name: EVENTS.ACCOUNT_SWITCH_INITIATED });
    analytics.reset();

    // Clear all auth state
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('org-storage');

    // Clear onboarding cache
    sessionStorage.removeItem('onboarding_github_orgs');
    sessionStorage.removeItem('onboarding_db_orgs');
    sessionStorage.removeItem('auth_return_url');
    sessionStorage.removeItem('refresh_org_id');

    set({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoggingIn: false,
    });

    // Redirect to the appropriate provider with forced re-auth
    try {
      const currentOrg = useOrgStore.getState().currentOrg;
      const isAdo = currentOrg?.provider_type === 'azure_devops';

      if (isAdo) {
        const { authorization_url, state } = await api.getAdoAuthUrl();
        sessionStorage.setItem('ado_oauth_state', state);
        window.location.href = authorization_url;
      } else {
        const { authorization_url } = await api.getGitHubAuthUrlForSwitchAccount();
        window.location.href = authorization_url;
      }
    } catch (error) {
      logError('Failed to get switch account URL:', error);
      // Fallback to login page
      window.location.href = '/login';
    }
  },

  loadUser: async () => {
    try {
      const user = await api.getCurrentUser();
      set({ user });
    } catch (error) {
      logError('Failed to load user:', error);
      get().clearSession();
    }
  },

  // Ends a session that died on its own (expired/revoked token) SILENTLY: it
  // is not a user-initiated logout, and calling logout() would fire a phantom
  // LOGOUT analytics event + reset() on every stale-token page load.
  clearSession: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    useOrgStore.getState().reset();
    set({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoggingIn: false,
    });
  },

  setTokens: (accessToken: string, refreshToken: string) => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    set({
      accessToken,
      isAuthenticated: true,
    });
  },

  clearLoggingIn: () => {
    set({ isLoggingIn: false });
  },
}));

// The API client ends the session here when a request proves it dead on a
// public page, where it deliberately does not redirect to /login.
setSessionEndedHandler(() => useAuthStore.getState().clearSession());
